/**
 * Contexto agregado do CRM — definição única (hook + Context).
 * O provider de dados fica em CrmProviders.tsx.
 */
import { createContext, useContext } from 'react';
import type {
    Lead,
    CrmUser,
    FunnelStage,
    Funnel,
    SubscriptionPlan,
    ClientProfile,
    WhatsappConnection,
    MetaAccount,
    Subscription,
    Contact,
} from '../types';
import type { SaveLeadOptions } from './leads/hooks/useLeadManagement';

export interface CrmDataContextType {
    leads: Lead[];
    contacts: Contact[];
    crmUsers: CrmUser[];
    funnelStages: FunnelStage[];
    funnels: Funnel[];
    selectedFunnelId: string | null;
    subscriptionPlans: SubscriptionPlan[];
    clientProfile: ClientProfile | null;
    whatsappConnections: WhatsappConnection[];
    metaAccounts: MetaAccount[];
    subscription: Subscription | null;
    isLoading: boolean;
    updateLeadStage: (leadId: string, newStageId: string) => void;
    saveLead: (leadToSave: Lead, meta?: SaveLeadOptions) => Promise<Lead | null>;
    deleteLead: (leadId: string) => Promise<void>;
    setSelectedFunnelId: (id: string | null) => void;
    refreshLeads: (funnelIdOverride?: string | null) => Promise<void>;
    saveClientProfile: (profile: ClientProfile) => void;
    updateSubscription: (planId: string, userCount: number, billingCycle: 'monthly' | 'annually') => void;
    refreshContacts: () => Promise<void>;
    updateContact: (id: string, contactData: Partial<Contact>) => Promise<Contact>;
    createContact: (contactData: Omit<Contact, 'id'>) => Promise<Contact>;
    deleteContact: (id: string) => Promise<void>;
}

export const CrmDataContext = createContext<CrmDataContextType | undefined>(undefined);

export function useCrmData(): CrmDataContextType {
    const context = useContext(CrmDataContext);
    if (!context) {
        throw new Error('useCrmData must be used within a CrmDataProvider');
    }
    return context;
}
